import { Platform } from "react-native";

// ─── Colors ──────────────────────────────────────────────────────────

export const colors = {
  // Core warm palette
  warm: "#D4A574",
  warmDark: "#C4956A",
  warmLight: "#FFF8F0",
  coral: "#E8927C",
  teal: "#5B9E9E",
  blue: "#7B8EB5",
  green: "#82D4A8",
  gold: "#F5C882",
  deepBrown: "#3B1A08",
  greyBrown: "#6B5548",

  // Biology illustration palette
  biologyCream: "#FFF8EC",
  biologyFrost: "#F1D1A6",
  biologyAmber: "#F4B34F",
  biologyCopper: "#B76545",
  biologyBrown: "#542F27",

  // Text hierarchy
  text: "#4A3F35",
  textMid: "#6B5E54",
  textLight: "#8B7E72",
  textFaint: "#B5ADA5",

  // Surfaces
  bg: "#FFF8F0",
  bgEnd: "#F5EDE4",
  border: "#F0EBE5",
  surface: "#FFFFFF",

  // Phase colors
  phases: {
    exploring: "#D4A574",
    active_cycle: "#E8927C",
    post_retrieval: "#F5C882",
    storage: "#7B8EB5",
  } as const,

  // Evidence strength
  evidence: {
    strong: "#82D4A8",
    moderate: "#F5C882",
    emerging: "#7B8EB5",
    mixed: "#E8927C",
  } as const,

  // Semantic
  success: "#82D4A8",
  warning: "#F5C882",
  error: "#E8927C",
  info: "#7B8EB5",
} as const;

// ─── Semantic Colors ─────────────────────────────────────────────────

export const semanticColors = {
  background: {
    app: colors.bg,
    appEnd: colors.bgEnd,
  },
  surface: {
    default: colors.surface,
    raised: colors.surface,
    sheet: colors.surface,
    subtle: "rgba(255, 255, 255, 0.36)",
    glass: "rgba(255, 255, 255, 0.3)",
    input: "rgba(255, 255, 255, 0.6)",
    transparent: "transparent",
  },
  border: {
    default: colors.border,
    subtle: "rgba(42, 37, 34, 0.08)",
    strong: "rgba(42, 37, 34, 0.12)",
    glass: "rgba(255, 255, 255, 0.5)",
    focus: colors.warm,
  },
  text: {
    primary: colors.text,
    secondary: colors.textMid,
    tertiary: colors.textLight,
    faint: colors.textFaint,
    inverse: colors.surface,
  },
  /** Role-based colors for the canonical ordinary title/subtitle pair. */
  typography: {
    primary: "#2A2522",
    supporting: "#8A7D74",
  },
  action: {
    primary: {
      background: colors.deepBrown,
      foreground: colors.surface,
      pressedOverlay: "rgba(0, 0, 0, 0.06)",
    },
    destructive: {
      background: colors.coral,
      foreground: colors.surface,
      pressedOverlay: "rgba(232, 146, 124, 0.14)",
    },
  },
  overlay: {
    scrim: "rgba(0, 0, 0, 0.5)",
    lightScrim: "rgba(0, 0, 0, 0.3)",
    pressed: "rgba(42, 37, 34, 0.06)",
    hover: "rgba(42, 37, 34, 0.04)",
  },
  status: {
    success: colors.green,
    warning: colors.gold,
    danger: colors.coral,
    info: colors.blue,
    neutral: colors.textLight,
  },
} as const;

// ─── Typography ──────────────────────────────────────────────────────

export const typography = {
  families: {
    serif: "InstrumentSerif_400Regular",
    sans: "DMSans_400Regular",
    sansMedium: "DMSans_500Medium",
    sansBold: "DMSans_700Bold",
    mono: "DMMono_400Regular",
  },
  sizes: {
    xs: 10,
    sm: 11,
    base: 13,
    md: 14,
    lg: 16,
    xl: 20,
    "2xl": 24,
    "3xl": 28,
    "4xl": 32,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.4,
    relaxed: 1.55,
  },
} as const;

// ─── Spacing (8-point grid) ──────────────────────────────────────────

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 32,
  "4xl": 40,
  "5xl": 48,
} as const;

// ─── Border Radii ────────────────────────────────────────────────────

export const radii = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  full: 999,
} as const;

// ─── Shadows ─────────────────────────────────────────────────────────

export const shadows = {
  sm: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 1,
  },
  md: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  lg: {
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 4,
  },
} as const;

// ─── Spring Configs ─────────────────────────────────────────────────

export const springs = {
  /** Modal/sheet entrance & exit */
  sheet: { damping: 20, stiffness: 200, mass: 0.8 },
  /** Pressable press-in / release */
  press: { damping: 15, stiffness: 300 },
  /** Toggle knob spring */
  toggle: { damping: 15, stiffness: 220, mass: 0.6 },
  /** Playful bounce (badges, celebrations) */
  bounce: { damping: 12, stiffness: 180, mass: 0.6 },
} as const;

// ─── Layout Constants ────────────────────────────────────────────────

export const HEADER_INSET = 34;
export const SCREEN_PADDING_TOP = Platform.OS === "web" ? HEADER_INSET : 0;

// ─── Glass Card Style ────────────────────────────────────────────────

export const GLASS_CARD_STYLE = {
  borderRadius: 16,
  backgroundColor: semanticColors.surface.glass,
  borderWidth: 1,
  borderColor: semanticColors.border.glass,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 8 },
  shadowOpacity: 0.08,
  shadowRadius: 32,
  ...(Platform.OS === "web" && {
    backdropFilter: "blur(60px)",
    WebkitBackdropFilter: "blur(60px)",
  }),
} as Record<string, unknown>;

// ─── Backward-compatible EMBER export ────────────────────────────────

export const EMBER = {
  colors: {
    warm: colors.warm,
    warmDark: colors.warmDark,
    warmLight: colors.warmLight,
    coral: colors.coral,
    teal: colors.teal,
    blue: colors.blue,
    green: colors.green,
    gold: colors.gold,
    text: colors.text,
    textMid: colors.textMid,
    textLight: colors.textLight,
    textFaint: colors.textFaint,
    bg: colors.bg,
    bgEnd: colors.bgEnd,
    border: colors.border,
    surface: colors.surface,
  },
} as const;
